import { inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import {
  SSO_AUTH_APP_PATH_FAST_REFRESH,
  SSO_AUTH_APP_PATH_LANDING,
  SSO_AUTH_APP_PATH_LOGIN,
  SSO_LOGOUT_IN_PROGRESS_KEY,
  SSO_REDIRECT_ATTEMPTS_KEY,
  SSO_REDIRECT_IN_PROGRESS_KEY,
  SSO_RETURN_URL_KEY,
  SSO_TARGET_APP_KEY,
  SSO_TOKEN_REFRESH_REQUIRED_KEY
} from '@core/sso/sso.constants';
import { I_API_POST_verificaRegistrazione } from '@models/apiresponses.models';
import {
  IApiExchangeTicketResponse,
  IApiToken,
  IExchangeTicketNoProfileRequest,
  IExchangeTicketRequest,
  ILoginCredentials,
  IResetPWD,
  IResetPWDResponse,
  ISSOToken,
  IToken,
  RESET_PWD_APP_SOURCE
} from '@models/auth/auth.model';
import { IUser, RuoloAttivo } from '@models/user.model';
import { ApiService } from '@services/api/api.service';
import { AppStore } from '@services/store/app.store';
import { UserRepository } from '@services/user.repository.service';
import { UserService } from '@services/user.service';
import { Console } from '@shared/providers/custom-logger';
import { catchError, finalize, first, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { environment } from 'src/environments/environment';
import { IApiErrorResponse, IApiStandardResponse } from '../models/api/api.models';
import { IRegistrazione, IVerificaRegistrazione } from '../models/signup.model';

/**
 * Servizio per la gestione dell'autenticazione e delle operazioni relative all'utente.
 *
 * Questo servizio si occupa di tutte le operazioni di autenticazione e autorizzazione,
 * inclusi login, logout, refresh del token, registrazione utente e gestione password.
 *
 * Funzionalità offerte:
 * - Autenticazione utente (login/logout)
 * - Gestione e refresh dei token di accesso
 * - Registrazione e verifica di nuovi utenti
 * - Gestione completa del ciclo di vita delle password (reset, modifica, verifica)
 * - Verifica della disponibilità email per nuove registrazioni
 * - Gestione delle sessioni utente
 *
 * @example
 * constructor(private authService: AuthService) {}
 *
 * // Esempio di login
 * this.authService.login(credentials).subscribe(token => {
 *   // Gestione del token ricevuto
 * });
 */
@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private log_label = 'AuthService';
  private refreshInFlight$: Observable<IToken> | null = null;
  private signOutInFlight = false;
  private apiService = inject(ApiService);
  private appStore = inject(AppStore);
  private router = inject(Router);
  private injector = inject(Injector);

  /**
   * Recupera i dati dell'utente corrente autenticato.
   * 
   * Gestisce automaticamente gli errori di autenticazione, rete e validazione.
   * In caso di token non valido (401), effettua automaticamente il logout.
   *
   * @returns Observable contenente i dati dell'utente
   * @throws {Error} Se la richiesta fallisce, l'utente non è autenticato o si verificano errori di rete
   */
  me() {
    return this.apiService.get<{ data: IUser }>('user').pipe(
      first(),
      map((response) => response.data),
      catchError((error) => {
        Console.log(`[${this.log_label}] Errore nel recupero dati utente:`, error, 'err');
        
        // Gestione specifica per errori di autenticazione
        if (error.status === 401) {
          Console.log(`[${this.log_label}] Token non valido, logout automatico`, '', 'warn');
          this.appStore.clearAuth();
          this.router.navigateByUrl('/login', { replaceUrl: true });
          return throwError(() => new Error('Sessione scaduta, effettua nuovamente il login'));
        }
        
        // Gestione errori di rete/server
        if (error.status >= 500) {
          return throwError(() => new Error('Errore del server, riprova più tardi'));
        }
        
        // Gestione errori di validazione
        if (error.status === 422) {
          return throwError(() => new Error('Dati utente non validi'));
        }
        
        // Gestione errori generici
        const errorMessage = error.error?.message || error.message || 'Errore nel recupero dati utente';
        return throwError(() => new Error(errorMessage));
      })
    );
  }

  /**
   * Effettua il login dell'utente con le credenziali fornite.
   *
   * @param loginData Le credenziali di accesso dell'utente (email e password)
   * @returns Observable contenente il token di autenticazione e i dati dell'utente
   * @throws {Error} Se le credenziali non sono valide o la richiesta fallisce
   */
  login(loginData: ILoginCredentials): Observable<IToken> {
    Console.log('AuthService.login - Before device_id assignment:', {
        device_id: this.appStore.device_id(),
        loginData: loginData
    }, 'std');

    loginData.device_name = this.appStore.device_id() ?? '';

    Console.log('AuthService.login - After device_id assignment:', {
        device_name: loginData.device_name,
        loginData: loginData
    }, 'std');

    return this.apiService
      .post<IApiToken, ILoginCredentials>('login', loginData)
      .pipe(
        first(),
        tap((token) => {
            Console.log('AuthService.login - Token received:', token, 'succ');
            this.appStore.setToken(token.data);
            this.appStore.setIsSso(false);
        }),
        map((response) => {
            Console.log('AuthService.login - Handling token:', response.data, 'std');
            return this.handleToken(response.data);
        }),
        catchError((error: IApiErrorResponse) => {
            Console.log('AuthService.login - Error details:', error, 'err');
            return throwError(() => error);
        })
      );
  }

  /**
   * Effettua il logout dell'utente e pulisce i dati di sessione.
   *
   * In caso di errore durante il logout, procede comunque con la pulizia locale
   * e il reindirizzamento alla pagina di login.
   *
   * @returns Observable che completa quando il logout è terminato
   * @throws {Error} Se si verifica un errore durante la richiesta di logout
   */
  logout() {
    Console.log('AuthService.logout - Starting logout process', '', 'std');

    return this.apiService
      .post<any, void>('logout', undefined)
      .pipe(
        tap((response) => {
          Console.log('AuthService.logout - API response received:', response, 'std');
        }),
        map((response) => {
          Console.log('AuthService.logout - Processing response', '', 'std');
          return response || {};
        }),
        first(),
        tap(() => {
          Console.log('AuthService.logout - Clearing auth and navigating', '', 'std');
          this.cleanupChatbotWidget();
          this.appStore.clearAuth();
          this.router.navigateByUrl('/login', { replaceUrl: true });
        }),
        catchError((error) => {
          Console.log('AuthService.logout - Error occurred:', error, 'err');
          this.cleanupChatbotWidget();
          this.appStore.clearAuth();
          this.router.navigateByUrl('/login', { replaceUrl: true });
          return of(null);
        })
      );
  }

  /**
   * Disconnessione in base al tipo di sessione:
   * - **SSO** (`is_sso === true`): redirect alla landing di Auth-App senza chiamare `/logout` API.
   * - **Legacy** (login form): chiama l'API `logout` e reindirizza a `/login`.
   */
  signOut(): Observable<unknown> {
    if (this.appStore.is_sso() === true) {
      return this.logoutToAuthApp();
    }
    return this.logout();
  }

  /**
   * Esegue il logout una sola volta anche se invocato da più punti contemporaneamente.
   * Utile per guard/effect che possono reagire allo stesso evento di sessione.
   */
  triggerSignOut(): void {
    if (this.signOutInFlight) {
      return;
    }
    this.signOutInFlight = true;
    this.signOut()
      .pipe(finalize(() => {
        this.signOutInFlight = false;
      }))
      .subscribe({
        error: (error) => {
          Console.log('AuthService.triggerSignOut - errore durante logout', error, 'err');
        }
      });
  }

  /**
   * Aggiorna il token di autenticazione utilizzando il refresh token corrente.
   *
   * **Operazioni eseguite:**
   * 1. Recupera il token corrente dallo store dell'applicazione
   * 2. Estrae il refresh_token dal token memorizzato
   * 3. Invia una richiesta API per ottenere un nuovo token di accesso
   * 4. Salva automaticamente il nuovo token nello store tramite handleToken()
   *
   * **Gestione degli errori:**
   * - Se non viene trovato alcun token locale, lancia un errore
   * - Se riceve un errore 401 (Unauthorized), significa che anche il refresh token è scaduto:
   *   l'errore viene propagato; l'`httpInterceptor` (modalità legacy) esegue pulizia e redirect a `/login`.
   * - Per altri errori HTTP, rilancia l'eccezione per permettere la gestione upstream
   *
   * @returns Observable contenente il nuovo token di autenticazione aggiornato
   * @throws {Error} Se non viene trovato un token locale valido
   * @throws {Error} Se la richiesta di refresh fallisce per motivi diversi dall'autorizzazione
   *
   * @example
   * ```typescript
   * this.authService.refreshToken().subscribe({
   *   next: (newToken) => Console.log('Token aggiornato:', newToken, 'succ'),
   *   error: (error) => Console.log('Errore nel refresh:', error, 'err')
   * });
   * ```
   */
  refreshToken() {
    if (this.refreshInFlight$) {
      return this.refreshInFlight$;
    }

    const tokenStrigify = this.appStore.token();
    const refresh$ = of(tokenStrigify).pipe(
      map((itoken) => {
        if (!itoken) throw new Error('No token found');
        return itoken.refresh_token;
      }),
      switchMap((token) => {
        return this.apiService.post<IApiToken, { refresh_token: string }>(
          'refresh-token',
          {
            refresh_token: token,
          }
        ).pipe(
          catchError((error) => {
            if (error.status === 401) {
              Console.log('AuthService.refreshToken - 401 received (refresh scaduto), propagazione errore', '', 'warn');
            }
            return throwError(() => error);
          })
        );
      }),
      map((responseRefreshToken) =>
        this.handleToken(responseRefreshToken.data)
      ),
      catchError((error) => {
        Console.log('AuthService.refreshToken - General error:', error, 'err');
        return throwError(() => error);
      }),
      finalize(() => {
        this.refreshInFlight$ = null;
      }),
      shareReplay(1)
    );

    this.refreshInFlight$ = refresh$;
    return refresh$;
  }

  // /**
  //  * Pulisce tutti i dati di autenticazione dallo storage locale.
  //  * Rimuove il token, username e altri dati specifici dell'applicazione.
  //  * @returns Promise che si risolve quando la pulizia è completata
  //  */
  // async clearLocalStorage() {
  //   localStorage.removeItem('token');
  //   localStorage.removeItem('username');

  //   // Pulizia dello storage della specifica App
  //   this.localStorage.clear(StorageFile.UTENTE);
  //   this.localStorage.clear(StorageFile.PREMI);
  //   this.localStorage.clear(StorageFile.SISTEMA);

  //   await this.localStorage.setItem(
  //     StorageFile.SISTEMA,
  //     environment.INTRO_KEY,
  //     'true'
  //   );
  // }

  /**
   * Gestisce il salvataggio del token nel sistema.
   *
   * Verifica la validità del token e lo memorizza nello store dell'applicazione.
   *
   * @param response Il token da salvare
   * @returns Il token validato e salvato
   * @throws {Error} Se il token non è valido o mancante
   *
   * @private
   */
  private handleToken(response: IToken) {
    if (!response.token || !response.token_id || !response.token_type)
      throw new Error('No token found');
    this.appStore.setToken(response);
    return response;
  }

  /**
   * Invia una richiesta per il reset della password.
   *
   * Invia un'email all'utente con le istruzioni per reimpostare la password.
   *
   * @param data I dati necessari per il reset della password (email, "premi" come default per indicacre chi ha richiesto il reset)
   * @returns Observable contenente la risposta del server
   * @throws {Error} Se la richiesta fallisce o l'email non è valida
   */
  resetPWD(data: IResetPWD) {
    let log_pref = this.log_label + '.resetPWD() ';
    const payload: IResetPWD = {
      ...data,
      app_source: data.app_source ?? RESET_PWD_APP_SOURCE,
    };
    return this.apiService
      .post<IResetPWDResponse, IResetPWD>('/password/email', payload)
      .pipe(
        catchError((error: IApiErrorResponse) => {
          const errorMessage =
            log_pref + (error.error || 'Errore nel reset della password.');
          return throwError(() => ({
            error: {
              message: errorMessage,
            },
          }));
        })
      );
  }

  /**
   * Verifica se un'email è disponibile per la registrazione di un nuovo utente.
   *
   * Controlla che l'email non sia già utilizzata nel sistema.
   *
   * @param email L'email da verificare
   * @returns Observable contenente la risposta di disponibilità
   * @throws {Error} Se l'email è già in uso o la verifica fallisce
   */
  verificaNuovaUtenza(email: string): Observable<IApiStandardResponse<unknown>> {
    let log_pref = this.log_label + '.verificaNuovaUtenza() ';
    return this.apiService
      .post<IApiStandardResponse<unknown>, { email: string }>('user/registrazione/disponibile', {
        email: email,
      })
      .pipe(
        map((response: IApiStandardResponse<unknown>) => {
          return response;
        }),
        catchError((error: IApiErrorResponse) => {
          error.message =
            log_pref + (error?.message || ' Email già utilizzata.');
          return throwError(() => error);
        })
      );
  }

  /**
   * Verifica i dati di registrazione dell'utente.
   *
   * Controlla la validità dei dati forniti per la registrazione.
   *
   * @param input I dati di verifica della registrazione
   * @returns Observable contenente la risposta di verifica
   * @throws {Error} Se la verifica fallisce o i dati non sono validi
   */
  verificaRegistrazione(
    input: IVerificaRegistrazione
  ): Observable<I_API_POST_verificaRegistrazione> {
    let log_pref = this.log_label + '.verificaRegistrazione() ';
    return this.apiService
      .post<I_API_POST_verificaRegistrazione, IVerificaRegistrazione>(
        'user/registrazione/verifica',
        input
      )
      .pipe(
        catchError((error: IApiErrorResponse) => {
          let errorMessage = log_pref + 'Errore nella verifica della registrazione';

          return throwError(() => error);
        })
      );
  }

  /**
   * Effettua la registrazione di un nuovo utente nel sistema.
   *
   * Crea un nuovo account utente con i dati forniti.
   *
   * @param input I dati di registrazione dell'utente
   * @returns Observable contenente la risposta della registrazione
   * @throws {Error} Se la registrazione fallisce o i dati non sono validi
   */
  registrazione(input: IRegistrazione): Observable<IApiStandardResponse<unknown>> {
    let log_pref = this.log_label + '.registrazione() ';
    return this.apiService
      .post<IApiStandardResponse<unknown>, IRegistrazione>('user/registrazione', input)
      .pipe(
        map((response: IApiStandardResponse<unknown>) => {
          return response;
        }),
        catchError((error: IApiErrorResponse) => {
          error.message =
            log_pref +
            (error?.message || ' Errore nella registrazione utente.');
          return throwError(() => error);
        })
      );
  }

  /**
   * Modifica la password dell'utente autenticato.
   *
   * Richiede la password attuale e la nuova password.
   *
   * @param credentials Le credenziali per la modifica della password
   * @returns Observable contenente la risposta della modifica
   * @throws {Error} Se la modifica fallisce o le credenziali non sono valide
   */
  changePassword(credentials: string) {
    let log_pref = this.log_label + '.changePassword() ';
    return this.apiService
      .post<IResetPWDResponse, string>('/password/modifica', credentials)
      .pipe(
        map((response: IResetPWDResponse) => {
          return response;
        }),
        catchError((error: IApiErrorResponse) => {
          error.message =
            log_pref +
            (error?.message || ' Errore nella modifica della password.');
          return throwError(() => error);
        })
      );
  }

  /**
   * Effettua il reset della password dell'utente.
   *
   * Utilizza il token di reset ricevuto via email per impostare una nuova password.
   *
   * @param credentials I dati necessari per il reset (email, password, conferma password, token)
   * @returns Observable contenente la risposta del reset
   * @throws {Error} Se il reset fallisce o i dati non sono validi
   */
  resetPassword(credentials: {
    email: string;
    password: string;
    password_confirmation: string;
    token: string;
  }) {
    let log_pref = this.log_label + '.resetPassword() ';
    return this.apiService
      .post<
        IResetPWDResponse,
        {
          email: string;
          password: string;
          password_confirmation: string;
          token: string;
        }
      >('/password/reset', credentials)
      .pipe(
        catchError((error: IApiErrorResponse) => {
          error.message =
            log_pref + (error?.message || ' Errore nel reset della password.');
          return throwError(() => error);
        })
      );
  }

  /**
   * Determina il target_app corretto dall'URL corrente, dal sessionStorage, o dall'environment.
   * Priorità:
   * 1. target_app salvato in sessionStorage (quando viene ricevuto il ticket)
   * 2. target_app estratto dall'URL corrente (es. /convenzionato -> convenzionato)
   * 3. environment.TARGET_APP come fallback
   */
  determineTargetApp(urlPath?: string): string {
    // Prima prova a recuperare il target_app salvato in sessionStorage
    const savedTargetApp = sessionStorage.getItem(SSO_TARGET_APP_KEY);
    if (savedTargetApp) {
      return savedTargetApp;
    }
    
    try {
      // Prova a estrarre il target_app dall'URL corrente o dal path fornito
      const pathname = urlPath || window.location.pathname;
      const trimmedPath = pathname.trim();
      
      // Se il pathname contiene un segmento valido (es. /convenzionato, /manifestazione)
      // usa quello come target_app
      if (trimmedPath && trimmedPath !== '/' && trimmedPath !== '/auth' && trimmedPath !== '/app') {
        // Rimuovi lo slash iniziale e prendi il primo segmento
        const segments = trimmedPath.split('/').filter(s => s);
        if (segments.length > 0) {
          const firstSegment = segments[0];
          // Verifica che non sia un percorso di sistema e sia un target_app valido
          if (!['auth', 'app', 'private', 'login', 'landing', 'signup', 'recovery', 'intro', 'app-download'].includes(firstSegment)) {
            // Verifica che sia uno dei target_app validi
            if (['manifestazione', 'convenzionato', 'promotore'].includes(firstSegment)) {
              return firstSegment;
            }
          }
        }
      }
      
      // Fallback: usa environment.TARGET_APP
      return environment.TARGET_APP;
    } catch {
      return environment.TARGET_APP;
    }
  }

  /**
   * Determina la landing page di default in base al targetApp.
   * 
   * @param targetApp Il target_app (manifestazione, convenzionato, promotore)
   * @param returnUrl Opzionale: URL di ritorno salvato
   * @returns L'URL della landing page
   */
  determineDefaultLanding(targetApp?: string, returnUrl?: string | null): string {
    // Se c'è un returnUrl valido, usalo
    if (returnUrl && returnUrl.startsWith('/')) {
      return returnUrl;
    }
    
    // Determina la landing in base al targetApp
    if (targetApp === 'manifestazione') {
      return '/app/utente';
    } else if (targetApp === 'promotore') {
      return '/app/promotore-page';
    } else if (targetApp === 'convenzionato') {
      return '/app/convenzionato-page';
    }
    
    // Fallback: usa environment o default
    return (environment as { SSO_DEFAULT_LANDING?: string }).SSO_DEFAULT_LANDING ?? '/app/utente';
  }

  /**
   * Costruisce l'URL di redirect verso Auth-App (login, fast-refresh o landing).
   * Unico punto di costruzione per ridurre duplicazione e migliorare portabilità.
   */
  buildAuthAppRedirectUrl(path: 'login' | 'fast-refresh' | 'landing', extraQuery?: Record<string, string>): string {
    const authAppUrl = this.appStore.authAppUrlResolved();
    const targetApp = (path === 'login' || path === 'fast-refresh') ? this.determineTargetApp() : undefined;
    
    const pathMap = { login: SSO_AUTH_APP_PATH_LOGIN, 'fast-refresh': SSO_AUTH_APP_PATH_FAST_REFRESH, landing: SSO_AUTH_APP_PATH_LANDING };
    
    try {
      const url = new URL(authAppUrl);
      url.pathname = pathMap[path];
      url.search = '';
      url.hash = '';
      if ((path === 'login' || path === 'fast-refresh') && targetApp) {
        url.searchParams.set('target_app', targetApp);
      }
      if (extraQuery) {
        Object.entries(extraQuery).forEach(([k, v]) => url.searchParams.set(k, v));
      }
      return url.toString();
    } catch {
      const base = authAppUrl.replace(/\/+$/, '');
      const pathPart = pathMap[path];
      const params = new URLSearchParams(extraQuery as Record<string, string> || {});
      if ((path === 'login' || path === 'fast-refresh') && targetApp) {
        params.set('target_app', targetApp);
      }
      const search = params.toString() ? `?${params.toString()}` : '';
      return `${base}${pathPart}${search}`;
    }
  }

  /**
   * Redirect verso Auth-App /login con target_app. Incrementa i tentativi e salva returnUrl.
   * Prima di chiamare, verificare isRedirectBlocked() per evitare loop.
   * Non esegue nulla se siamo in fase di logout SSO (logoutToAuthApp sta già reindirizzando alla landing).
   */
  redirectToAuthAppLogin(returnUrl?: string): void {
    // Se siamo già in fase di logout o redirect SSO, non fare redirect multipli
    const isLogoutInProgress = sessionStorage.getItem(SSO_LOGOUT_IN_PROGRESS_KEY) === 'true';
    let isRedirectInProgress = sessionStorage.getItem(SSO_REDIRECT_IN_PROGRESS_KEY) === 'true';
    /**
     * `SSO_REDIRECT_IN_PROGRESS_KEY` resta true se l'utente interrompe il giro (Indietro, tab, navigazione bloccata)
     * dopo che abbiamo impostato il flag ma prima del caricamento completo di Auth-App. Senza token e senza
     * logout SSO in corso, è un residuo: altrimenti il return sotto impedisce `window.location` e l'app resta bloccata.
     * Non resettare se l'URL contiene `ticket=`: è il ritorno da Auth-App verso `handleSsoCallback` / exchange in corso
     * (stesso flag può restare true dopo un 401 SSO finché lo scambio non ripulisce lo storage).
     */
    const urlHasSsoTicket = this.router.url.includes('ticket=');
    if (isRedirectInProgress && !isLogoutInProgress && !this.appStore.token() && !urlHasSsoTicket) {
      sessionStorage.removeItem(SSO_REDIRECT_IN_PROGRESS_KEY);
      isRedirectInProgress = false;
    }

    if (isLogoutInProgress || isRedirectInProgress) {
      if (environment.ENABLE_ALERTS) {
        alert(`[app] [AuthService] redirectToAuthAppLogin ignorato\nLogout in corso: ${isLogoutInProgress ? 'SÌ' : 'NO'}\nRedirect in corso: ${isRedirectInProgress ? 'SÌ' : 'NO'}`);
      } else {
        Console.log('AuthService - redirectToAuthAppLogin ignorato', { isLogoutInProgress, isRedirectInProgress }, 'warn');
      }
      return;
    }
    const raw = sessionStorage.getItem(SSO_REDIRECT_ATTEMPTS_KEY);
    const attempts = Math.max(0, parseInt(raw ?? '0', 10)) + 1;
    sessionStorage.setItem(SSO_REDIRECT_ATTEMPTS_KEY, String(attempts));
    if (returnUrl) {
      sessionStorage.setItem(SSO_RETURN_URL_KEY, returnUrl);
    }
    // Rimuove il flag di logout se presente (siamo in fase di login, non logout)
    sessionStorage.removeItem(SSO_LOGOUT_IN_PROGRESS_KEY);
    // Imposta il flag per evitare redirect multipli durante il redirect verso auth-app
    sessionStorage.setItem(SSO_REDIRECT_IN_PROGRESS_KEY, 'true');
    this.appStore.setAuthAppUrl(this.appStore.authAppUrlResolved());
    const redirectUrl = this.buildAuthAppRedirectUrl('login');
    if (environment.ENABLE_ALERTS) {
      alert(`[app] [AuthService] redirectToAuthAppLogin\nTentativo: ${attempts}\nReturnUrl: ${returnUrl || 'N/A'}\nRedirect a: ${redirectUrl}\nFlag redirect impostato: SÌ`);
    } else {
      Console.log('AuthService - redirectToAuthAppLogin', { attempts, returnUrl, redirectUrl }, 'info');
    }
    window.location.href = redirectUrl;
  }

  /** True se i tentativi di redirect verso Auth-App hanno superato il massimo (evita loop). */
  isRedirectBlocked(): boolean {
    const max = environment.SSO_REDIRECT_MAX_ATTEMPTS ?? 3;
    const raw = sessionStorage.getItem(SSO_REDIRECT_ATTEMPTS_KEY);
    const attempts = Math.max(0, parseInt(raw ?? '0', 10));
    return attempts >= max;
  }

  /** Resetta il conteggio tentativi di redirect (es. prima di "Riprova"). */
  resetRedirectAttempts(): void {
    sessionStorage.removeItem(SSO_REDIRECT_ATTEMPTS_KEY);
  }

  /**
   * Scambia un ticket SSO con il backend e ottiene il token.
   * Usa sempre `sso/exchange-ticket` (header Origin; niente `-no-origin`).
   * id_profilo è obbligatorio (multiutenza).
   */
  exchangeTicket(ticket: string, idProfilo: number, targetApp?: string): Observable<IToken> {
    const endpoint = environment.SSO_EXCHANGE_TICKET_ENDPOINT;
    const targetAppToUse = targetApp || environment.TARGET_APP;
    const body: IExchangeTicketRequest = {
      ticket,
      id_profilo: idProfilo
    };
    if (environment.ENABLE_ALERTS) {
      alert(`[app] [AuthService] exchangeTicket iniziato\nEndpoint: ${endpoint}\ntargetApp: ${targetApp || environment.TARGET_APP}\nid_profilo: ${idProfilo}\nToken corrente nello store: ${this.appStore.token() ? 'PRESENTE' : 'ASSENTE'}`);
    } else {
      Console.log('AuthService - exchangeTicket iniziato', { endpoint, targetApp: targetApp || environment.TARGET_APP, idProfilo, hasToken: !!this.appStore.token() }, 'info');
    }
    return this.apiService
      .post<IApiExchangeTicketResponse, IExchangeTicketRequest>(
        endpoint,
        body
      )
      .pipe(
        first(),
        map((res) => res.data),
        tap((data: ISSOToken) => {
          if (environment.ENABLE_ALERTS) {
            alert(`[app] [AuthService] exchangeTicket - Token ricevuto dal backend\nToken ID: ${data.token_id}\nToken salvato nello store...`);
          } else {
            Console.log('AuthService - exchangeTicket token ricevuto', { tokenId: data.token_id }, 'succ');
          }
          this.handleSsoToken(data);
        }),
        switchMap(() => {
          const userService = this.injector.get(UserService);
          return userService.getApiUser().pipe(
            map(() => {
              const token = this.appStore.token();
              if (!token) {
                throw new Error('Token not found after exchangeTicket');
              }
              if (environment.ENABLE_ALERTS) {
                alert(`[app] [AuthService] exchangeTicket completato\nToken nello store: PRESENTE\nToken ID: ${token.token_id}`);
              } else {
                Console.log('AuthService - exchangeTicket completato', { tokenId: token.token_id }, 'succ');
              }
              return token;
            })
          );
        }),
        catchError((err) => {
          Console.log('AuthService.exchangeTicket - error', err, 'err');
          if (environment.ENABLE_ALERTS) {
            const currentOrigin = window.location.origin;
            alert(
              `[app] [AuthService] exchangeTicket errore` +
              `\nErrore: ${err.message || JSON.stringify(err)}` +
              `\nEndpoint: ${endpoint}` +
              `\nTargetApp: ${targetAppToUse}` +
              `\nOrigin corrente: ${currentOrigin}`
            );
          } else {
            Console.log('AuthService.exchangeTicket - error', err, 'err');
          }
          return throwError(() => err);
        })
      );
  }

  /**
   * Scambia un ticket SSO con il backend per il flusso \"senza profilo\".
   * Usa l'endpoint dedicato `sso/exchange-ticket-no-profile` che non richiede id_profilo.
   * targetApp è opzionale e viene dedotto dall'URL/env se non fornito.
   */
  exchangeTicketNoProfile(ticket: string, targetApp?: string): Observable<IToken> {
    const endpoint = 'sso/exchange-ticket-no-profile';
    const targetAppToUse = targetApp || environment.TARGET_APP;
    const body: IExchangeTicketNoProfileRequest = {
      ticket,
      target_app: targetAppToUse,
      // opzionale: device_name può essere usato lato backend per tracciare il client
      device_name: this.appStore.device_id() ?? undefined
    };
    if (environment.ENABLE_ALERTS) {
      alert(`[app] [AuthService] exchangeTicketNoProfile iniziato\nEndpoint: ${endpoint}\ntargetApp: ${targetAppToUse}\nToken corrente nello store: ${this.appStore.token() ? 'PRESENTE' : 'ASSENTE'}`);
    } else {
      Console.log('AuthService - exchangeTicketNoProfile iniziato', { endpoint, targetAppToUse, hasToken: !!this.appStore.token() }, 'info');
    }
    return this.apiService
      .post<IApiExchangeTicketResponse, IExchangeTicketNoProfileRequest>(
        endpoint,
        body
      )
      .pipe(
        first(),
        map((res) => res.data),
        tap((data: ISSOToken) => {
          if (environment.ENABLE_ALERTS) {
            alert(`[app] [AuthService] exchangeTicketNoProfile - Token ricevuto dal backend\nToken ID: ${data.token_id}\nToken salvato nello store...`);
          } else {
            Console.log('AuthService - exchangeTicketNoProfile token ricevuto', { tokenId: data.token_id }, 'succ');
          }
          this.handleSsoToken(data);
        }),
        switchMap(() => {
          const userService = this.injector.get(UserService);
          return userService.getApiUser().pipe(
            map(() => {
              const token = this.appStore.token();
              if (!token) {
                throw new Error('Token not found after exchangeTicketNoProfile');
              }
              if (environment.ENABLE_ALERTS) {
                alert(`[app] [AuthService] exchangeTicketNoProfile completato\nToken nello store: PRESENTE\nToken ID: ${token.token_id}`);
              } else {
                Console.log('AuthService - exchangeTicketNoProfile completato', { tokenId: token.token_id }, 'succ');
              }
              return token;
            })
          );
        }),
        catchError((err) => {
          Console.log('AuthService.exchangeTicketNoProfile - error', err, 'err');
          if (environment.ENABLE_ALERTS) {
            const currentOrigin = window.location.origin;
            alert(
              `[app] [AuthService] exchangeTicketNoProfile errore` +
              `\nErrore: ${err.message || JSON.stringify(err)}` +
              `\nEndpoint: ${endpoint}` +
              `\nTargetApp: ${targetAppToUse}` +
              `\nOrigin corrente: ${currentOrigin}`
            );
          } else {
            Console.log('[app] [AuthService] exchangeTicketNoProfile errore', err.message || JSON.stringify(err), 'err');
          }
          return throwError(() => err);
        })
      );
  }

  /**
   * Gestisce la callback SSO: inizializza device e profilo, scambia il ticket, ripulisce sessionStorage
   * e restituisce l'URL dove navigare (returnUrl salvato, defaultLanding, o /app/utente).
   */
  handleSsoCallback(
    ticket: string,
    idProfilo: number,
    options?: { defaultLanding?: string; targetApp?: string }
  ): Observable<string> {
    if (environment.ENABLE_ALERTS) {
      alert(`[app] [AuthService] handleSsoCallback iniziato\nTicket: ${ticket.substring(0, 20)}...\nid_profilo: ${idProfilo}\ntargetApp: ${options?.targetApp || 'N/A'}`);
    } else {
      Console.log('AuthService - handleSsoCallback iniziato', { ticket: ticket.substring(0, 20), idProfilo, targetApp: options?.targetApp }, 'info');
    }
    this.appStore.initializeDeviceId();
    /** Passaggio da sessione legacy a SSO: elimina il token login prima dello scambio ticket */
    if (this.appStore.token() && this.appStore.is_sso() !== true) {
      this.appStore.clearAuth();
    }
    this.appStore.setIdProfilo(idProfilo);
    return this.exchangeTicket(ticket, idProfilo, options?.targetApp).pipe(
      switchMap(() => {
        this.appStore.setAuthAppUrl(this.appStore.authAppUrlResolved());
        this.appStore.setHasSeenIntro(true); // Evita redirect alle pagine intro dopo SSO
        sessionStorage.removeItem(SSO_REDIRECT_ATTEMPTS_KEY);
        sessionStorage.removeItem(SSO_TARGET_APP_KEY);
        sessionStorage.removeItem(SSO_LOGOUT_IN_PROGRESS_KEY); // Rimuove il flag di logout quando torniamo da auth-app
        sessionStorage.removeItem(SSO_REDIRECT_IN_PROGRESS_KEY); // Rimuove il flag di redirect quando torniamo da auth-app
        sessionStorage.removeItem(SSO_TOKEN_REFRESH_REQUIRED_KEY); // Rimuove il flag di refresh token dopo che il nuovo token è stato salvato con successo
        // Imposta il ruolo attivo in base al profilo scelto (convenzionato/promotore/manifestazione)
        if (options?.targetApp) {
          this.injector.get(UserRepository).setActiveRole(options.targetApp as RuoloAttivo);
        }
        const returnUrl = sessionStorage.getItem(SSO_RETURN_URL_KEY);
        sessionStorage.removeItem(SSO_RETURN_URL_KEY);
        const landing = this.determineDefaultLanding(options?.targetApp, returnUrl) || options?.defaultLanding || '/app/utente';
        if (environment.ENABLE_ALERTS) {
          alert(`[app] [AuthService] handleSsoCallback completato\nToken scambiato: SÌ\nReturnUrl salvato: ${returnUrl || 'N/A'}\nLanding finale: ${landing}`);
        } else {
          Console.log('AuthService - handleSsoCallback completato', { returnUrl, landing }, 'succ');
        }
        return of(landing);
      }),
      catchError((err) => {
        Console.log('AuthService.handleSsoCallback - error', err, 'err');
        // Rimuove il flag anche in caso di errore per evitare che rimanga impostato
        sessionStorage.removeItem(SSO_TOKEN_REFRESH_REQUIRED_KEY);
        if (environment.ENABLE_ALERTS) {
          const currentOrigin = window.location.origin;
          alert(
            `[app] [AuthService] handleSsoCallback errore` +
            `\nErrore: ${err.message || JSON.stringify(err)}` +
            `\nTargetApp: ${options?.targetApp || 'N/A'}` +
            `\nOrigin corrente: ${currentOrigin}`
          );
        } else {
          Console.log('[app] [AuthService] handleSsoCallback errore', err.message || JSON.stringify(err), 'err');
        }
        return throwError(() => err);
      })
    );
  }

  /**
   * Gestisce la callback SSO per il flusso \"senza profilo\":
   * inizializza il device, scambia il ticket senza id_profilo e restituisce la landing.
   */
  handleSsoCallbackNoProfile(
    ticket: string,
    options?: { defaultLanding?: string; targetApp?: string }
  ): Observable<string> {
    const targetApp = options?.targetApp;
    if (environment.ENABLE_ALERTS) {
      alert(`[app] [AuthService] handleSsoCallbackNoProfile iniziato\nTicket: ${ticket.substring(0, 20)}...\ntargetApp: ${targetApp || 'N/A'}`);
    } else {
      Console.log('AuthService - handleSsoCallbackNoProfile iniziato', { ticket: ticket.substring(0, 20), targetApp }, 'info');
    }
    this.appStore.initializeDeviceId();
    if (this.appStore.token() && this.appStore.is_sso() !== true) {
      this.appStore.clearAuth();
    }
    return this.exchangeTicketNoProfile(ticket, targetApp).pipe(
      switchMap(() => {
        this.appStore.setAuthAppUrl(this.appStore.authAppUrlResolved());
        this.appStore.setHasSeenIntro(true); // Evita redirect alle pagine intro dopo SSO
        sessionStorage.removeItem(SSO_REDIRECT_ATTEMPTS_KEY);
        sessionStorage.removeItem(SSO_TARGET_APP_KEY);
        sessionStorage.removeItem(SSO_LOGOUT_IN_PROGRESS_KEY);
        sessionStorage.removeItem(SSO_REDIRECT_IN_PROGRESS_KEY);
        sessionStorage.removeItem(SSO_TOKEN_REFRESH_REQUIRED_KEY);
        // Imposta il ruolo attivo se targetApp è noto
        if (targetApp) {
          this.injector.get(UserRepository).setActiveRole(targetApp as RuoloAttivo);
        }
        const returnUrl = sessionStorage.getItem(SSO_RETURN_URL_KEY);
        sessionStorage.removeItem(SSO_RETURN_URL_KEY);
        const landing = this.determineDefaultLanding(targetApp, returnUrl) || options?.defaultLanding || '/app/utente';
        if (environment.ENABLE_ALERTS) {
          alert(`[app] [AuthService] handleSsoCallbackNoProfile completato\nToken scambiato: SÌ\nReturnUrl salvato: ${returnUrl || 'N/A'}\nLanding finale: ${landing}`);
        } else {
          Console.log('AuthService - handleSsoCallbackNoProfile completato', { returnUrl, landing }, 'succ');
        }
        return of(landing);
      }),
      catchError((err) => {
        Console.log('AuthService.handleSsoCallbackNoProfile - error', err, 'err');
        sessionStorage.removeItem(SSO_TOKEN_REFRESH_REQUIRED_KEY);
        if (environment.ENABLE_ALERTS) {
          const currentOrigin = window.location.origin;
          alert(
            `[app] [AuthService] handleSsoCallbackNoProfile errore` +
            `\nErrore: ${err.message || JSON.stringify(err)}` +
            `\nTargetApp: ${targetApp || 'N/A'}` +
            `\nOrigin corrente: ${currentOrigin}`
          );
        } else {
          Console.log('[app] [AuthService] handleSsoCallbackNoProfile errore', err.message || JSON.stringify(err), 'err');
        }
        return throwError(() => err);
      })
    );
  }

  private handleSsoToken(data: ISSOToken): void {
    if (!data?.token || data.token_id == null) throw new Error('No token from exchange-ticket');
    const token: IToken = {
      token_id: String(data.token_id), // Converti a string per compatibilità con IToken
      token: data.token,
      token_type: data.token_type ?? 'Bearer',
      // Campi opzionali per SSO (non presenti nel token SSO)
      token_expiration_minutes: '',
      refresh_token: ''
    };
    if (environment.ENABLE_ALERTS) {
      alert(`[app] [AuthService] handleSsoToken - Salvataggio token nello store\nToken ID: ${token.token_id}\nToken type: ${token.token_type}\nToken salvato: SÌ`);
    } else {
      Console.log('AuthService - handleSsoToken token salvato', { tokenId: token.token_id, tokenType: token.token_type }, 'succ');
    }
    this.appStore.setToken(token);
    this.appStore.setIsSso(true);
  }

  /**
   * Logout "soft": pulisce stato locale e reindirizza alla landing di Auth-App.
   * NON chiama l'API logout (il token condiviso con Auth-App resta valido).
   * NON pulisce il token dal localStorage per permettere ad Auth-App di verificare l'autenticazione.
   * Usato dal menu "Esci".
   * 
   * Nota: Il token viene mantenuto nel localStorage dell'app client. Quando Auth-App viene visitata,
   * può verificare l'autenticazione tramite il backend (il token è condiviso tra le app tramite il backend).
   * Auth-App ha un localStorage separato, quindi il token deve essere condiviso tramite il backend.
   */
  logoutToAuthApp(): Observable<null> {
    // Imposta i flag PRIMA di pulire il token per evitare che l'interceptor faccia redirect multipli
    // durante il logout quando ci sono chiamate API ancora in corso che falliscono con 401
    sessionStorage.setItem(SSO_LOGOUT_IN_PROGRESS_KEY, 'true');
    sessionStorage.setItem(SSO_REDIRECT_IN_PROGRESS_KEY, 'true');
    
    // Come in primeng-apollo-20: puliamo tutto incluso il token
    // Auth-App gestirà l'autenticazione tramite il backend quando viene visitata
    this.appStore.clearAuth();
    sessionStorage.removeItem(SSO_REDIRECT_ATTEMPTS_KEY);
    sessionStorage.removeItem(SSO_RETURN_URL_KEY);
    sessionStorage.removeItem(SSO_TARGET_APP_KEY);
    
    const redirectUrl = this.buildAuthAppRedirectUrl('landing');
    if (environment.ENABLE_ALERTS) {
      alert(`[app] [AuthService] logoutToAuthApp\nRedirect a: ${redirectUrl}\nToken pulito: SÌ\nFlag logout impostato: SÌ\nFlag redirect impostato: SÌ`);
    } else {
      Console.log('AuthService - logoutToAuthApp', { redirectUrl }, 'info');
    }
    window.location.href = redirectUrl;
    return of(null);
  }

  /**
   * Invalida il token per testare il flusso SSO di refresh token.
   * 
   * Questo metodo è disponibile solo in ambiente di sviluppo e simula un token scaduto
   * cancellando il token dallo store e forzando una chiamata API che fallirà con 401.
   * L'interceptor HTTP catturerà il 401 e attiverà il redirect ad Auth-App per il refresh.
   * 
   * **Utilizzo**: Solo per sviluppo e testing del ciclo SSO.
   * 
   * @returns Observable che completa quando il token è stato invalidato
   */
  invalidateTokenForTesting(): Observable<void> {
    if (environment.production) {
      Console.log('AuthService - invalidateTokenForTesting chiamato in produzione, ignorato', '', 'warn');
      return of(void 0);
    }

    Console.log('AuthService - invalidateTokenForTesting - Invalidazione token per test SSO', '', 'info');

    const currentToken = this.appStore.token();
    if (!currentToken) {
      Console.log('AuthService - invalidateTokenForTesting - Nessun token presente', '', 'warn');
      if (environment.ENABLE_ALERTS) {
        alert(`[app] [AuthService] invalidateTokenForTesting\nNessun token presente da invalidare`);
      }
      return of(void 0);
    }

    // NON cancellare il token: sostituirlo con uno invalido.
    // Se cancellassimo il token, la richiesta passerebbe dal branch !token dell'interceptor
    // e andremmo a /login invece che al 401 handler. Con token invalido, la richiesta
    // va con Authorization header → backend risponde 401 → interceptor → fast-refresh.
    const invalidToken: IToken = {
      token_id: currentToken.token_id,
      token: 'invalid-for-testing',
      token_expiration_minutes: '',
      refresh_token: '',
      token_type: 'Bearer',
    };
    this.appStore.setToken(invalidToken);

    if (environment.ENABLE_ALERTS) {
      alert(`[app] [AuthService] invalidateTokenForTesting\nToken sostituito con valore invalido\nProssima chiamata API → 401 → redirect a /sso-fast`);
    } else {
      Console.log('AuthService - invalidateTokenForTesting - Token invalidato, prossima chiamata API attiverà 401 → fast-refresh', '', 'info');
    }

    // Forza una chiamata API che fallirà con 401 (token invalido)
    return this.me().pipe(
      map(() => void 0),
      catchError((error) => {
        // L'errore 401 è atteso, l'interceptor gestisce il redirect a /sso-fast
        Console.log('AuthService - invalidateTokenForTesting - Chiamata API fallita come previsto, interceptor gestirà redirect a fast-refresh', '', 'info');
        return of(void 0);
      })
    );
  }

    /**
   * Rimuove il widget/chatbot Xenioo dal DOM (icona e overlay) se presente.
   */
    private cleanupChatbotWidget() {
      try {
        const ww: any = window as any;
        if (ww && ww.xenioowebchat) {
          try { typeof ww.xenioowebchat.Close === 'function' && ww.xenioowebchat.Close(); } catch {}
          try { typeof ww.xenioowebchat.Hide === 'function' && ww.xenioowebchat.Hide(); } catch {}
          try { typeof ww.xenioowebchat.Stop === 'function' && ww.xenioowebchat.Stop(); } catch {}
          try { typeof ww.xenioowebchat.Destroy === 'function' && ww.xenioowebchat.Destroy(); } catch {}
        }
      } catch {}
  
      try {
        document
          .querySelectorAll("script[src*='xenioo.com/plugin/xenioo.js'], script[data-id='xenioo']")
          .forEach((el) => el.parentNode && el.parentNode.removeChild(el as Node));
  
        document
          .querySelectorAll("#xenioo, #xenioowebchat, [id*='xenioo'], [class*='xenioo'], iframe[src*='xenioo']")
          .forEach((el) => el.parentNode && el.parentNode.removeChild(el));
      } catch {}
    }
}
